import type { Transformer } from '@lexical/markdown';
import { I_AWAIT_NODE } from './await';
import { I_MACRO_QUOTE } from './classedBlock';
import { CUSTOM_TRANSFORMERS } from './customTransformers';
import { I_IMAGE_CONSTRAINED, IMAGE } from './image';
import {
  E_BLOCK_EQUATION_NODE,
  E_INLINE_EQUATION_NODE,
  E_MULTILINE_BLOCK_EQUATION_NODE,
  I_EQUATION_NODE,
} from './katex';
import {
  E_CONTACT_MENTION,
  E_DATE_MENTION,
  E_DOCUMENT_CARD,
  E_DOCUMENT_MENTION,
  E_GROUP_MENTION,
  E_USER_MENTION,
  I_CONTACT_MENTION,
  I_DATE_MENTION,
  I_DOCUMENT_CARD,
  I_DOCUMENT_MENTION,
  I_GROUP_MENTION,
  I_THEME_MENTION,
  I_USER_MENTION,
} from './mentions';
import { E_SNAPSHOT_NODE, I_SNAPSHOT_NODE } from './snapshot';
import { E_TABLE_NODE, I_TABLE_NODE } from './tables';
import {
  BR_TAG_TO_LINE_BREAK,
  HR,
  HTML_ENTITY_TRANSFORMERS,
  isConversionOnlyTransformer,
  LINK_XML,
  MARK_XML,
  PRESERVE_LINES,
  SEARCH_MATCH,
} from './transformers';
import { UNKNOWN_MENTION } from './unknownMention';
import { I_VIDEO } from './video';
import { E_WATERMARK, I_WATERMARK } from './watermark';

export { isConversionOnlyTransformer };

/**
 * Internal transformers for converting markdown between Lexical state and markdown.
 * The internal format uses XML-based syntax to represent nodes that fall outside of
 * standard markdown syntax.
 */
export const INTERNAL_TRANSFORMERS: Transformer[] = [
  I_SNAPSHOT_NODE, // Must be before mentions to avoid matching inner tags in snapshot content
  PRESERVE_LINES,
  LINK_XML, // Prefer internal xml link to handle []() in link text
  MARK_XML,
  SEARCH_MATCH,
  HR,
  I_VIDEO,
  I_IMAGE_CONSTRAINED,
  IMAGE, // Standard markdown images (fallback)
  I_USER_MENTION,
  I_GROUP_MENTION,
  I_DOCUMENT_MENTION,
  I_DOCUMENT_CARD,
  I_CONTACT_MENTION,
  I_DATE_MENTION,
  I_AWAIT_NODE,
  I_TABLE_NODE,
  I_MACRO_QUOTE,
  I_EQUATION_NODE,
  I_THEME_MENTION,
  I_WATERMARK,
  ...CUSTOM_TRANSFORMERS,
  UNKNOWN_MENTION, // Must be last to act as fallback for unrecognized XML tags
];

/**
 * External transformers for converting Lexical to and from to GitHub Flavored (ish) Markdown.
 */
export const EXTERNAL_TRANSFORMERS: Transformer[] = [
  HR,
  MARK_XML,
  I_VIDEO,
  IMAGE,
  BR_TAG_TO_LINE_BREAK,
  E_TABLE_NODE,
  E_USER_MENTION,
  E_GROUP_MENTION,
  I_DOCUMENT_MENTION, // for chat attachments
  E_DOCUMENT_MENTION,
  I_DOCUMENT_CARD, // for internal representation
  E_DOCUMENT_CARD,
  E_CONTACT_MENTION,
  E_DATE_MENTION,
  // order matters
  E_MULTILINE_BLOCK_EQUATION_NODE,
  E_BLOCK_EQUATION_NODE,
  E_INLINE_EQUATION_NODE,
  E_WATERMARK,
  E_SNAPSHOT_NODE,
  ...HTML_ENTITY_TRANSFORMERS,
  ...CUSTOM_TRANSFORMERS,
  UNKNOWN_MENTION, // Must be last to act as fallback for unrecognized XML tags
];

/**
 * Complete set of transformers supporting both internal and external markdown operations.
 */
export const ALL_TRANSFORMERS: Transformer[] = [
  I_SNAPSHOT_NODE, // Must be before mentions to avoid matching inner tags in snapshot content
  PRESERVE_LINES,
  LINK_XML, // Prefer internal xml link to handle []() in link text
  MARK_XML,
  SEARCH_MATCH,
  HR,
  I_VIDEO,
  I_IMAGE_CONSTRAINED,
  IMAGE,
  BR_TAG_TO_LINE_BREAK,
  I_TABLE_NODE,
  E_TABLE_NODE,
  E_SNAPSHOT_NODE,
  I_USER_MENTION,
  E_USER_MENTION,
  I_GROUP_MENTION,
  E_GROUP_MENTION,
  I_DOCUMENT_MENTION,
  E_DOCUMENT_MENTION,
  I_DOCUMENT_CARD,
  E_DOCUMENT_CARD,
  I_CONTACT_MENTION,
  E_CONTACT_MENTION,
  I_DATE_MENTION,
  E_DATE_MENTION,
  I_AWAIT_NODE,
  I_MACRO_QUOTE,
  I_EQUATION_NODE,
  I_THEME_MENTION,
  I_WATERMARK,
  E_WATERMARK,
  // order matters
  E_MULTILINE_BLOCK_EQUATION_NODE,
  E_BLOCK_EQUATION_NODE,
  E_INLINE_EQUATION_NODE,
  ...HTML_ENTITY_TRANSFORMERS,
  ...CUSTOM_TRANSFORMERS,
  UNKNOWN_MENTION, // Must be last to act as fallback for unrecognized XML tags
];
